/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { receiptService } from '../../services/ReceiptService';
import { notificationService } from '../../services/NotificationService';
import { useNavigate } from 'react-router-dom';
import { downloadReceiptPDF } from '../../utils/PDFExport';
import {
  ArrowDownIcon,
  RefreshIcon,
  SearchIcon,
  ReceiptRefundIcon,
  CurrencyDollarIcon,
  DocumentTextIcon
} from '@heroicons/react/outline';
import { Mail, Check, X, Loader } from 'lucide-react';

const ReceiptsList = ({ userType }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [receipts, setReceipts]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Send-receipt state per row: { [receiptId]: 'idle' | 'confirming' | 'sending' | 'sent' }
  const [sendState, setSendState] = useState({});

  const fetchReceipts = async () => {
    if (!user?.uid) { setError('User not authenticated'); setLoading(false); return; }
    setLoading(true);
    try {
      let result;
      if (userType === 'MPS') result = await receiptService.getMPSReceipts(user.uid);
      else if (userType === 'EO') result = await receiptService.getEOReceipts(user.uid);

      if (result.success) { setReceipts(result.receipts); setError(null); }
      else setError(result.message);
    } catch (err) {
      setError('Failed to load receipts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReceipts(); }, [user, userType]);

  const formatCurrency = (cents) => `$${(cents / 100).toFixed(2)}`;
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filteredReceipts = receipts.filter((receipt) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      receipt.eventName?.toLowerCase().includes(search) ||
      receipt.transactionId?.toLowerCase().includes(search) ||
      receipt.mpsBusinessName?.toLowerCase().includes(search)
    );
  });

  const totalAmount = filteredReceipts.reduce(
    (sum, r) => sum + (r.eoAmount || r.totalAmount || 0),
    0,
  );

  /* ── send receipt helpers ── */
  const startSend = (id) => setSendState(prev => ({ ...prev, [id]: 'confirming' }));
  const cancelSend = (id) => setSendState(prev => ({ ...prev, [id]: 'idle' }));

  const confirmSend = async (receipt) => {
    setSendState(prev => ({ ...prev, [receipt.id]: 'sending' }));
    try {
      await notificationService.sendPaymentReceiptEmail({
        mpsEmail:        receipt.mpsEmail,
        mpsBusinessName: receipt.mpsBusinessName,
        eventName:       receipt.eventName,
        totalAmount:     receipt.totalAmount,
        platformFee:     receipt.platformFee,
        transactionId:   receipt.transactionId,
        receiptId:       receipt.id,
        eoUserId:        user?.uid,
      });
      setSendState(prev => ({ ...prev, [receipt.id]: 'sent' }));
      setTimeout(() => setSendState(prev => ({ ...prev, [receipt.id]: 'idle' })), 3000);
    } catch (err) {
      console.error('Send receipt error', err);
      setSendState(prev => ({ ...prev, [receipt.id]: 'idle' }));
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center py-16 transition-opacity duration-300">
          <p className="text-sm text-gray-500">Loading receipts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {userType === 'MPS' ? 'My Payment Receipts' : 'Receipts'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {userType === 'MPS' ? 'View your payment history' : 'Track vendor payments'}
          </p>
        </div>
        <button
          onClick={fetchReceipts}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RefreshIcon className="h-4 w-4 mr-1.5" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-6">
          {error}
        </div>
      )}

      {/* Stats */}
      {receipts.length > 0 && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  {userType === 'MPS' ? 'Total Paid' : 'Total Collected'}
                </p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(totalAmount)}</p>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DocumentTextIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Total Receipts</p>
                <p className="text-lg font-semibold text-gray-900">{filteredReceipts.length}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      {receipts.length > 0 && (
        <div className="mb-6">
          <div className="relative">
            <SearchIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search receipts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      )}

      {/* Content */}
      {filteredReceipts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <ReceiptRefundIcon className="w-7 h-7 text-gray-400" />
          </div>
          <h3 className="text-base font-medium text-gray-900 mb-1">
            {searchTerm ? 'No matching receipts' : 'No receipts yet'}
          </h3>
          <p className="text-sm text-gray-500 text-center max-w-sm">
            {searchTerm
              ? 'Try adjusting your search to find receipts.'
              : userType === 'MPS'
              ? 'Your payment receipts will appear here after you register for events.'
              : 'When vendors pay to join your events, receipts will appear here.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
                {userType === 'EO' && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Vendor</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {userType === 'MPS' ? 'Amount' : 'Your Share'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Platform Fee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredReceipts.map((receipt) => {
                const state = sendState[receipt.id] || 'idle';
                return (
                  <tr key={receipt.id} className="group hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{receipt.eventName}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{receipt.transactionId}</div>
                    </td>
                    {userType === 'EO' && (
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <div className="text-sm text-gray-700">{receipt.mpsBusinessName || '—'}</div>
                        {receipt.mpsEmail && (
                          <div className="text-xs text-gray-400 mt-0.5">{receipt.mpsEmail}</div>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">
                        {userType === 'MPS'
                          ? formatCurrency(receipt.totalAmount)
                          : formatCurrency(receipt.eoAmount)}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="text-sm text-gray-600">{formatCurrency(receipt.platformFee)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{formatDate(receipt.createdAt)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {/* Download */}
                        <button
                          onClick={() => downloadReceiptPDF(receipt)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 transition-all opacity-0 group-hover:opacity-100"
                        >
                          <ArrowDownIcon className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Download</span>
                        </button>

                        {/* Send to Vendor — EO only */}
                        {userType === 'EO' && (
                          <>
                            {state === 'idle' && (
                              <button
                                onClick={() => startSend(receipt.id)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-grey-600 bg-grey-100 rounded-md hover:bg-grey-200 transition-colors"
                                title="Send receipt to vendor"
                              >
                                <Mail className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">Send</span>
                              </button>
                            )}

                            {state === 'confirming' && (
                              <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                                <span className="text-xs text-amber-800 hidden md:inline">
                                  {receipt.mpsEmail
                                    ? `Send to ${receipt.mpsEmail}?`
                                    : `Send to ${receipt.mpsBusinessName}?`}
                                </span>
                                <span className="text-xs text-amber-800 md:hidden">Send?</span>
                                <button
                                  onClick={() => confirmSend(receipt)}
                                  className="ml-1 p-0.5 text-green-600 hover:text-green-800 transition-colors"
                                  title="Confirm"
                                >
                                  <Check className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => cancelSend(receipt.id)}
                                  className="p-0.5 text-grey-400 hover:text-grey-600 transition-colors"
                                  title="Cancel"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            )}

                            {state === 'sending' && (
                              <div className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-grey-400">
                                <Loader className="h-3.5 w-3.5 animate-spin" />
                                <span className="hidden sm:inline">Sending…</span>
                              </div>
                            )}

                            {state === 'sent' && (
                              <div className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-600">
                                <Check className="h-3.5 w-3.5" />
                                Sent!
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReceiptsList;
